import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Dropdown, LinkAsButton } from '@printdeal/ui-library';

// Contexts
import OrderDetailContext from '../../contexts/OrderDetailContext';

// Types
import { SanitizedLineItem } from '../../../../../types/order/Order';

// Helpers
import { dispatchAccountEvent } from '../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';

// Action related files (Button, Hook)
import AddComplaintButton from '../LineItemActions/AddComplaint/AddComplaintButton';
import canAddComplaintHelper from '../LineItemActions/AddComplaint/CanAddComplaintHelper';
import CancelLineItemButton from '../LineItemActions/CancelLineItem/CancelLineItemButton';
import canCancelLineItemHelper from '../LineItemActions/CancelLineItem/CanCancelLineItemHelper';
import DownloadFilesButton from '../LineItemActions/DownloadFiles/DownloadFilesButton';
import useDownloadFiles from '../LineItemActions/DownloadFiles/useDownloadFiles';
import EditFilesButton from '../LineItemActions/EditFiles/EditFilesButton';
import useEditFiles from '../LineItemActions/EditFiles/useEditFiles';
import EditLineItemButton from '../LineItemActions/EditLineItem/EditLineItemButton';
import useEditLineItem from '../LineItemActions/EditLineItem/useEditLineItem';
import ExtendQuoteButton from '../LineItemActions/ExtendQuote/ExtendQuoteButton';
import useExtendQuote from '../LineItemActions/ExtendQuote/useExtendQuote';
import RemoveFilesButton from '../LineItemActions/RemoveFiles/RemoveFilesButton';
import canRemoveFilesHelper from '../LineItemActions/RemoveFiles/CanRemoveFilesHelper';

import useReOrder from '../../shared/Actions/ReOrderAction/useReOrder';
import OpenReOrderModalButton from '../../shared/Actions/ReOrderAction/OpenReOrderModalButton';

const MAX_AMOUNT_OF_VISIBLE_ACTIONS = 4;
const AMOUNT_ALWAYS_VISIBLE_ELEMENTS = 1;

interface LineItemCollapsedProps {
  lineItem: SanitizedLineItem;
}

const LineItemFooter = ({ lineItem }: LineItemCollapsedProps) => {
  // Load the order data from the context
  const { order } = useContext(OrderDetailContext);
  const [isDropdownOpen, setIsDropDownOpen] = useState(false);

  // Abort if no order data is available yet
  if (!order) {
    return null;
  }

  // Determine the availability of the various lineItem actions
  const canAddComplaint = canAddComplaintHelper(lineItem);
  const canCancelLineItem = canCancelLineItemHelper(lineItem);
  const canDownloadFiles = useDownloadFiles(lineItem);
  const canEditFiles = useEditFiles(lineItem, order);
  const canEditLineItem = useEditLineItem(lineItem, order);
  const canRemoveFiles = canRemoveFilesHelper(lineItem, order);
  const canExtendQuote = useExtendQuote(lineItem);
  const canReOrder = useReOrder([lineItem], order);

  const toggleDropdown = () => {
    if (isDropdownOpen) {
      dispatchAccountEvent('order_detail_interaction', 'open_menu_more_line_item_actions');
    } else {
      dispatchAccountEvent('order_detail_interaction', 'close_menu_more_line_item_actions');
    }
    setIsDropDownOpen(!isDropdownOpen);
  };

  const addComplaintButton = canAddComplaint.enabled && <AddComplaintButton lineItem={lineItem} order={order} key="addComplaint" />;
  const cancelLineItemButton = canCancelLineItem.enabled && (
    <CancelLineItemButton
      orderId={order.id}
      lineItemIds={[lineItem.id]}
      key="cancelLineItem"
    />
  );
  const downloadFilesButton = canDownloadFiles.enabled
    && <DownloadFilesButton fileInfo={canDownloadFiles.fileValidationData} key="downloadFiles" />;
  const editFilesButton = canEditFiles.enabled && canEditFiles.deliveryToolSettings && (
    <EditFilesButton
      orderId={order.id}
      lineItem={lineItem}
      deliveryToolSettings={canEditFiles.deliveryToolSettings}
      key="editFiles"
    />
  );
  const editLineItemButton = canEditLineItem.enabled && <EditLineItemButton lineItem={lineItem} orderId={order.id} key="editLineItem" />;
  const extendQuoteButton = canExtendQuote.enabled && <ExtendQuoteButton lineItem={lineItem} key="extendQuote" />;
  const removeFilesButton = canRemoveFiles.enabled && <RemoveFilesButton orderId={order.id} lineItem={lineItem} key="removeFiles" />;
  const reOrderButton = canReOrder.enabled && <OpenReOrderModalButton lineItems={[lineItem]} order={order} key="reOrderLineItem" />;

  const actions = [
    editLineItemButton,
    reOrderButton,
    extendQuoteButton,
    addComplaintButton,
    cancelLineItemButton,
    downloadFilesButton,
    removeFilesButton,
    editFilesButton,
  ].filter(Boolean);

  const alwaysShowCount = actions.length === AMOUNT_ALWAYS_VISIBLE_ELEMENTS + 1
    ? AMOUNT_ALWAYS_VISIBLE_ELEMENTS + 1
    : 1;
  const hideOnMobileCount = actions.length <= MAX_AMOUNT_OF_VISIBLE_ACTIONS + 1
    ? actions.length - 1
    : MAX_AMOUNT_OF_VISIBLE_ACTIONS - 1;

  // TODO AS-1035 Decide on the need for useMemo, and implement it if needed
  const alwaysShowActions = actions.slice(0, alwaysShowCount);
  const hideOnMobileActions = actions.slice(alwaysShowCount, alwaysShowCount + hideOnMobileCount);
  const alwaysHideActions = actions.slice(alwaysShowCount + hideOnMobileCount);

  return (
    <>
      <hr />
      <div className="pd-flex pd-items-center pd-gap-4">
        {alwaysShowActions}
        {hideOnMobileActions}
        {
          alwaysHideActions.length > 0 && (
            <Dropdown className="pd-flex">
              <Dropdown.Button
                as={LinkAsButton}
                variant="secondary"
                iconBefore="more"
                className="pd-text-xs"
                onClick={toggleDropdown}
              >
                <FormattedMessage id="general.dropdown.moreActions" defaultMessage="More Actions" />
              </Dropdown.Button>
              <Dropdown.Panel
                data-testid="as-dropdown"
                className="pd-w-52 pd-gap-4 pd-space-y-3"
                unmount={false}
              >
                {alwaysHideActions}
              </Dropdown.Panel>
            </Dropdown>
          )
        }
      </div>
    </>
  );
};

export default LineItemFooter;
